import Helper from "@/services/Helper";

class Cache
{
    /**
     * Stores a value in localStorage after serializing it.
     * @param name - The key under which to store the value.
     * @param value - The value to store.
     */
    set(name:string, value:any):void
    {
        let tmp = Helper.serialize(value);
        window.localStorage.setItem(name, tmp);
    }

    /**
     * Retrieves and deserializes a value from localStorage.
     * @param name - The key of the value to retrieve.
     * @returns The deserialized value or undefined if not found.
     */
    get(name:string)
    {
        let tmp = window.localStorage.getItem(name);

        if (tmp)
        {
            return Helper.deserialize(tmp);
        }

        return undefined;
    }

    /**
     * Appends a value to an array stored in localStorage.
     * If the stored value is not an array, it initializes a new array.
     * @param name - The key of the array to modify.
     * @param val - The value to append.
     */
    push(name:string, val:any):void  
    {
        let tmp = this.get(name);

        if (Helper.getType(tmp) !== "Array")
        {
            tmp = [];
        }

        tmp.push(val);
        this.set(name, tmp);
    }

    /**
     * Removes and returns the last element of an array stored in localStorage.
     * @param name - The key of the array to modify.
     * @returns The removed element or undefined if the value is not an array or is empty.
     */
    pop(name:string):any 
    {
        let tmp = this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            let val = tmp.pop();
            this.set(name, tmp);
            return val;
        }
        
        return undefined;
    }

    /**
     * Sets a value at a specific index in an array stored in localStorage.
     * @param name - The key of the array to modify.
     * @param index - The index at which to set the value.
     * @param val - The value to set.
     */
    setAt(name:string, index:number, val:any):void 
    {
        let tmp = this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            tmp[index] = val;
            this.set(name, tmp);
        }
    }

    /**
     * Removes elements from an array stored in localStorage and returns them.
     * @param name - The key of the array to modify.
     * @param start - The index at which to start removing elements.
     * @param deleteAmount - The number of elements to remove.
     * @returns The removed elements or undefined if the value is not an array.
     */
    splice(name:string, start:number, deleteAmount:number):Array<any> | undefined
    {
        let tmp = this.get(name);
        if (tmp && Helper.getType(tmp) === "Array")
        {
            let ret = tmp.splice(start, deleteAmount);
            this.set(name, tmp);
            return ret;
        }
        return undefined;
    }

    /**
     * Retrieves a value at a specific index from an array stored in localStorage.
     * @param name - The key of the array.
     * @param index - The index of the value to retrieve.
     * @returns The value at the specified index or undefined if the value is not an array.
     */
    getAt(name:string, index:number):any
    {
        let tmp = this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            return tmp[index];
        }

        return undefined;
    }

    /**
     * Iterates over an array stored in localStorage, executing a callback for each element.
     * Stops iteration if the callback returns false.
     * @param name - The key of the array to iterate.
     * @param callback - The function to execute for each element, receiving the element and its index.
     */
    each(name:string, callback:Function):void 
    {
        let tmp = this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (!ret) 
                {
                    break;
                }
            }

            this.set(name, tmp);
        }
    }

    /**
     * Searches an array stored in localStorage for an element that satisfies the callback.
     * @param name - The key of the array to search.
     * @param callback - The function to execute for each element, returning true for a match.
     * @returns The first matching element or undefined if no match is found or the value is not an array.
     */
    searchFor(name:string, callback:Function):any 
    {
        let tmp = this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (ret)
                {
                    return tmp[i];
                }
            }
        }

        return undefined;
    }

    /**
     * Finds the index of the first element in an array stored in localStorage that satisfies the callback.
     * @param name - The key of the array to search.
     * @param callback - The function to execute for each element, returning true for a match.
     * @returns The index of the first matching element or -1 if no match is found or the value is not an array.
     */
    indexOf(name:string, callback:Function):number
    {
        let tmp = this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (ret)
                {
                    return i;
                }
            }
        }

        return -1;
    }

    /**
     * Returns the length of an array stored in localStorage or the total number of keys in localStorage.
     * @param name - The key of the array to check, or undefined to count all keys in localStorage.
     * @returns The length of the array or the number of keys in localStorage.
     */
    length(name:string):number 
    {
        if (name)
        {
            let tmp = this.get(name);
            return (tmp && Helper.getType(tmp)) === "Array" ? 
                tmp.length : 0;
        }

        return window.localStorage.length;
    }

    /**
     * Removes a key and its value from localStorage, returning the value.
     * Clears all of localStorage if no key is provided.
     * @param name - The key to remove, or undefined to clear all of localStorage.
     * @returns The removed value or undefined if the key was not found.
     */
    unset(name:string):any 
    {
        if (name)
        {
            let ret = this.get(name);
            window.localStorage.removeItem(name);
            return ret;
        }

        else
        {
            // unset all
            window.localStorage.clear();
        }
    }

    /**
     * Removes an element at a specific index from an array stored in localStorage.
     * @param name - The key of the array to modify.
     * @param index - The index of the element to remove.
     */
    unsetAt(name:string, index:number) 
    {
        let tmp = this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            tmp.splice(index, 1);
            this.set(name, tmp);
        }
    }

    /**
     * Alias for unset. Removes a key and its value from localStorage, or clears all of localStorage.
     * @param name - The key to remove, or undefined to clear all of localStorage.
     * @returns The removed value or undefined if the key was not found.
     */
    clear = this.unset;

}

const singleton = new Cache();
export default singleton;